import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Box, Chip, Table, TableHead, TableRow, TableCell, TableBody, TableFooter, Alert } from "@mui/material";
import { Description as DescriptionIcon } from "@mui/icons-material";
import Papa from 'papaparse';

function MailingListPreview({ file, encoding, buttons, columns, setColumns, setNextDisabled }) {
    const { t } = useTranslation();
    const [previewData, setPreviewData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        setNextDisabled(false);
    }, [setNextDisabled]);

    /**
     * Essaie de deviner le contenu de chaque colonne
     */
    const initColumns = (results) => {
        if (!results) return;

        const keywords = {
            'lang': 'lang',
            'prenom': 'first_name',
            'prénom': 'first_name',
            'first': 'first_name',
            'nom': 'last_name',
            'last': 'last_name',
            'name': 'last_name',
            'gender': 'gender',
            'genre': 'gender',
            'sex': 'gender',
            'title': 'gender',
            'civilité': 'gender',
            'civilite': 'gender',
            'mail': 'email',
            'courriel': 'email',
            'phone': 'phone',
            'tel': 'phone',
            'tél': 'phone',
        };
        const newColumns = {};
        results.meta.fields.forEach(field => {
            const lowerField = field.toLowerCase();
            for (const [key, value] of Object.entries(keywords)) {
                if (lowerField.includes(key)) {
                    newColumns[field] = value;
                    break;
                }
            }
            // Pour les autres : extra
            if (!newColumns[field]) {
                newColumns[field] = 'extra';
            }
        });
        return newColumns;
    };

    /**
     * Traduit les noms des colonnes (pour les messages d'erreur)
     */
    const translateColumns = useCallback((columns) => {
        const translations = {
            'lang': t('Language'),
            'gender': t('Gender'),
            'first_name': t('First name'),
            'last_name': t('Last name'),
            'email': t('Email'),
            'phone': t('Phone'),
        };
        const newColumns = [];
        columns.forEach(col => {
            if (translations[col]) {
                newColumns.push('"' + translations[col] + '"');
            }
        });
        return newColumns;
    }, [t]);

    /**
     * Vérifie si les colonnes saisies sont valides :
     * - prénom + nom obligatoires
     * - pas de choix plusieurs fois pour les colonnes langue, civilité, prénom, nom, email, téléphone
     * Renvoie true si les colonnes sont valides, un message d'erreur sinon
     */
    const checkColumns = useCallback((columns) => {
        const requiredColumns = ['first_name', 'last_name'];
        const uniqueColumns = ['lang', 'gender', 'first_name', 'last_name', 'email', 'phone'];
        const columnsValues = Object.values(columns);
        const missingColumns = requiredColumns.filter(col => !columnsValues.includes(col));
        if (missingColumns.length > 1) {
            return t('The columns {{columns}} are required.', { columns: translateColumns(missingColumns).join(', ') });
        } else if (missingColumns.length === 1) {
            return t('The column {{column}} is required.', { column: translateColumns(missingColumns)[0] });
        }

        const uniqueColumnsValues = columnsValues.filter(col => uniqueColumns.includes(col));
        if (new Set(uniqueColumnsValues).size !== uniqueColumnsValues.length) {
            let duplicates = uniqueColumnsValues.filter((value, index, self) => self.indexOf(value) !== index);
            duplicates = duplicates.filter((value, index, self) => self.indexOf(value) === index);
            if (duplicates.length > 1) {
                return t('The columns {{columns}} have been selected several times.', { columns: translateColumns(duplicates).join(', ') });
            } else {
                return t('The column {{column}} has been selected several times.', { column: translateColumns(duplicates)[0] });
            }
        }

        return true;
    }, [t, translateColumns]);

    useEffect(() => {
        if (columns.length === 0) return;
        const check = checkColumns(columns);
        if (check !== true) {
            setError(check);
            setNextDisabled(true);
        } else {
            setError(null);
            setNextDisabled(false);
        }
    }, [columns, setNextDisabled, checkColumns]);

    useEffect(() => {
        if (!file) return;

        Papa.parse(file, {
            header: true,
            encoding: encoding,
            skipEmptyLines: true,
            preview: 50,
            complete: (results) => {
                setPreviewData(results);
                if (columns.length === 0) {
                    setColumns(initColumns(results));
                }
            },
            error: (error) => {
                console.error(error);
            }
        });
    }, [file, encoding, columns.length, setColumns]);

    const handleFieldChange = (event) => {
        setColumns({ ...columns, [event.target.name]: event.target.value });
    }

    return (
        <div>
            <Box sx={{ mb: 5 }}>
                {file && (
                    <Chip icon={<DescriptionIcon />} label={file.name} variant="outlined" sx={{ mb: 2 }} />
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {previewData && (
                    <Box sx={{ height: error ? 'calc(90vh - 324px)' : 'calc(90vh - 260px)', overflow: 'auto' }}>
                        <Table size="small" stickyHeader
                            sx={{
                                minWidth: '100%',
                                borderCollapse: 'collapse',
                                '& th, & td': {
                                    border: '1px solid #ddd',
                                },
                            }}>
                            <TableHead>
                                <TableRow>
                                    {previewData.meta.fields.map((field, index) => (
                                        <TableCell key={index}>
                                            {field}<br />
                                            <select onChange={handleFieldChange} name={field} value={columns[field]}>
                                                <option value="ignore">{t('Not imported')}</option>
                                                <option value="lang">{t('Language')}</option>
                                                <option value="gender">{t('Gender')}</option>
                                                <option value="first_name">{t('First name')}</option>
                                                <option value="last_name">{t('Last name')}</option>
                                                <option value="email">{t('Email')}</option>
                                                <option value="phone">{t('Phone')}</option>
                                                <option value="extra">{t('Custom data')}</option>
                                            </select>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {previewData.data.map((row, index) => (
                                    <TableRow key={index}>
                                        {previewData.meta.fields.map((field, index) => (
                                            <TableCell key={index}>
                                                {row.hasOwnProperty(field) ? row[field] : ''}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                            {previewData.data.length >= 50 && (
                                <TableFooter>
                                    <TableRow>
                                        <TableCell colSpan={previewData.meta.fields.length}>
                                            {t('This table only displays the first {{nb}} rows of the file.', { nb: 50 })}
                                        </TableCell>
                                    </TableRow>
                                </TableFooter>
                            )}
                        </Table>
                    </Box>
                )}
            </Box>
            {buttons}
        </div>
    );
}

export default MailingListPreview;